import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../utils/apiUtils';
import type { JoinApplication, JoinApplicationStatus } from '@myfutbolapp/shared';

export function useMatchJoinApplications(matchId: string | null, open: boolean) {
  const [rows, setRows] = useState<JoinApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    const res = await apiRequest<JoinApplication[]>(`/matches/${matchId}/join-applications`);
    if (res.error) {
      setError(res.error);
      setRows([]);
    } else {
      const all = Array.isArray(res.data) ? res.data : [];
      setRows(all.filter((r) => r.status === 'PENDING'));
    }
    setLoading(false);
  }, [matchId]);

  useEffect(() => {
    if (open && matchId) {
      void load();
    } else if (!open) {
      setRows([]);
    }
  }, [open, matchId, load]);

  const act = useCallback(
    async (applicationId: string, action: 'accept' | 'reject') => {
      if (!matchId) return { error: 'No match' };
      setBusy((b) => new Set(b).add(applicationId));
      const res = await apiRequest(
        `/matches/${matchId}/join-applications/${applicationId}/${action}`,
        { method: 'POST', body: action === 'reject' ? {} : undefined }
      );
      if (!res.error) {
        setRows((prev) => prev.filter((r) => r.id !== applicationId));
      }
      setBusy((b) => {
        const n = new Set(b);
        n.delete(applicationId);
        return n;
      });
      return res;
    },
    [matchId]
  );

  return { rows, loading, error, busy, refresh: load, act };
}

export type { JoinApplication, JoinApplicationStatus };
